import NachWrapper from '../../components/NachWrapper';
import { CountdownCircleTimer } from 'react-countdown-circle-timer';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'; // ✅ FIXED: Added useSearchParams
import { Spin } from 'antd'; // ✅ FIXED: Added Ant Design components for stability
import Lottie from 'lottie-react';
import Waiting from '../animation/waiting.json';
import Success from '../animation/success.json';
import Unsuccessful from '../animation/unsuccessful.json';
import { useEffect, useState } from 'react';
import { customer_routes } from '../routes';
import { getOrderStatusDetails, approveNachMandate } from 'api/nach';
import { NotFound } from 'common/components';

function AutoPayPage() {
  const [paymentStatus, setPaymentStatus] = useState('PENDING'); // PENDING, COMPLETED, FAILED
  const [orderStatusData, setOrderStatusData] = useState<any>({});
  const [isVerifying, setIsVerifying] = useState(false); // ✅ Added loading feedback state

  const navigate = useNavigate();
  const locationState = useLocation().state as { merchantOrderId: string; mandateId: number } | null;

  // ✅ THE CRITICAL RECOVERY TRICK:
  // If location state memory is wiped out by PhonePe's external redirection bounce,
  // we pull 'merchantOrderId' and 'mandateId' right out of the browser address bar parameters query string!
  const [searchParams] = useSearchParams();
  const merchantOrderId = locationState?.merchantOrderId || searchParams.get('merchantOrderId');
  const mandateId = locationState?.mandateId || Number(searchParams.get('mandateId')) || 0;

  // Structural boundary safety guard
  if (!merchantOrderId) {
    return <NotFound />;
  }

  // ✅ FIXED: Checks the order status IMMEDIATELY when the page loads, instead of waiting 90 seconds
  useEffect(() => {
    handleVerifyOrderStatus();
  }, [merchantOrderId]);

  // ✅ CONSOLIDATED SERVICE VALIDATOR (Triggered on load and on manual button click)
  const handleVerifyOrderStatus = () => {
    setIsVerifying(true);

    getOrderStatusDetails({
      orderId: merchantOrderId, // Sends your "MO..." string cleanly back to Java
      loggedInUserId: 0,
      mandateId: mandateId,
    }).then((res) => {
      console.log('Order status polling checkpoint response payload:', res);

      if (res.status === 'SUCCESS' && res.data) {
        console.log('✅ [AutoPayPage] Status query validated successfully. Binding response dataset payload data matrix:', res.data);

        setOrderStatusData(res.data);

        // Handle override administrative success/failure links returned by your controller
        if (res.data.mandateStatus === 'MANDATE_APPROVED' && res.data.successURL) {
          console.log('🔀 [AutoPayPage] Core administrative override path matched: MANDATE_APPROVED. Hijacking navigation to external window success URL:', res.data.successURL);
          window.location.href = res.data.successURL;
          return;
        } else if (res.data.mandateStatus === 'MANDATE_REJECTED' && res.data.failureURL) {
          console.log('🔀 [AutoPayPage] Core administrative override path matched: MANDATE_REJECTED. Hijacking navigation to external window failure URL:', res.data.failureURL);
          window.location.href = res.data.failureURL;
          return;
        }

        // ✅ MAP API GATEWAY RESPONSES DIRECTLY TO INTERFACE VIEW LAYOUT STATES
        if (res.data.state === 'COMPLETED' || res.data.state === 'SUCCESS') {
          const merchantSubscriptionId = res.data.paymentFlow?.merchantSubscriptionId || merchantOrderId;
          console.log(`🎉 [AutoPayPage] State verified as SUCCESSFUL. Launching approveNachMandate() persistence loop for MandateId: ${mandateId}, UMR/SubscriptionId: ${merchantSubscriptionId}`);
          approveNachMandate({ mandateId: mandateId, umrNo: merchantSubscriptionId }).then(() => {
            setPaymentStatus('COMPLETED'); // Swaps layout frame view to green success instantly
            handleSummaryPage(res.data, mandateId);
          }).catch(() => {
            setPaymentStatus('COMPLETED');
            handleSummaryPage(res.data, mandateId);
          });
        } else if (res.data.state === 'FAILED') {
          setPaymentStatus('FAILED'); // Swaps layout frame view to red unsuccessful instantly
          handleSummaryPage(res.data, mandateId);
        } else {
          setPaymentStatus('PENDING'); // Retains loader view state if processing is still running
        }
      }
    }).catch((err) => {
      console.error("System connection failure tracking payment ledger maps:", err);
    }).finally(() => {
      setIsVerifying(false);
    });
  };

  const handleSummaryPage = (orderStatusDetails: any, targetMandateId: any) => {
    // 4 seconds timeout allows the visual completion animation structures to loop smoothly before route switch
    setTimeout(() => {
      // Defensive fallback assignment arrays to protect against null reference pointer breaks inside paymentDetails
      const executionAttemptLogs = orderStatusDetails.paymentDetails?.[0] || {};

      const transactionData = {
        state: orderStatusDetails.state,
        amount: orderStatusDetails.amount,
        merchantOrderId: orderStatusDetails.merchantOrderId || merchantOrderId,
        orderId: orderStatusDetails.orderId,
        transactionId: executionAttemptLogs.transactionId || 'N/A',
        maxAmount: orderStatusDetails.paymentFlow?.maxAmount || 0,
        paymentMode: executionAttemptLogs.paymentMode || 'ACCOUNT_MANDATE',
        mandateId: targetMandateId,
      };

      // Navigate cleanly straight onto your final Avanse verification receipt summary page
      navigate(customer_routes.customer_payment_summary.to, {
        state: { ...transactionData },
        replace: true,
      });
    }, 4000);
  };

  const getMintsSeconds = (timer: any) => {
    if (timer === 0) {
      handleSummaryPage(orderStatusData, mandateId);
      return '00:00';
    }
    const seconds = timer % 60;
    const minutes = (timer - seconds) / 60;
    return `${minutes < 10 ? '0' + minutes : minutes}:${seconds < 10 ? '0' + seconds : seconds}`;
  };

  return (
    <NachWrapper title="Payment Confirmation" subTitle=" ">
      <Spin spinning={isVerifying}>
        <div style={{ marginRight: '30px' }}>

          {/* ⏳ VIEW LAYOUT 1: ACTIVE PENDING PANEL SCREEN FEATURING MANUAL ACTION BUTTON */}
          {paymentStatus === 'PENDING' ? (
            <>
              <div style={{ float: 'right', marginTop: '-90px', marginRight: '-35px' }}>
                <CountdownCircleTimer
                  isPlaying
                  duration={480}
                  strokeWidth={4}
                  size={55}
                  colors={['#2bf059', '#f0bb2b', '#F7B801', '#f03c2b', '#A30000']}
                  colorsTime={[360, 150, 60, 30, 0]}
                >
                  {({ remainingTime }) => <div>{getMintsSeconds(remainingTime)}</div>}
                </CountdownCircleTimer>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  alignItems: 'center',
                  minHeight: '320px',
                }}
              >
                <span>
                  <Lottie animationData={Waiting} style={{ height: '170px' }} />
                </span>
                <p style={{ fontSize: '15px', fontWeight: '600', marginTop: '10px' }}>
                  Payment is still pending. Please wait...
                </p>
                <p style={{ textAlign: 'center', fontWeight: '500', color: '#fc4903', marginBottom: '25px' }}>
                  Don't Close this window
                </p>

                {/* ✅ THE MANUAL STATUS ACTION OVERRIDE BUTTON */}
                {/* <Button
                  type="primary"
                  size="large"
                  onClick={handleVerifyOrderStatus}
                  style={{ background: '#fc4903', borderColor: '#fc4903', fontWeight: '600', width: '180px', borderRadius: '4px' }}
                >
                  Get Status
                </Button> */}
              </div>
            </>
          ) : null}
        </div>

        {/* 🎉 VIEW LAYOUT 2: COMPLETED PAYMENT CONFIRMATION SCREEN */}
        {paymentStatus === 'COMPLETED' ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'column',
              alignItems: 'center',
              minHeight: '320px',
            }}
          >
            <Lottie animationData={Success} style={{ height: '120px', fontWeight: '600' }} loop={false} />
            <p style={{ fontSize: '14px', fontWeight: '500', marginTop: '10px', color: '#fc4903' }}>
              Please don't refresh and don't close the window
            </p>
            <p style={{ fontSize: '14px', fontWeight: '500', marginTop: '10px' }}>
              Payment Successful
            </p>
          </div>
        ) : null}

        {/* ❌ VIEW LAYOUT 3: REFUSED OR CANCELLED MANDATE TRANSACTION WINDOW */}
        {paymentStatus === 'FAILED' ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'column',
              alignItems: 'center',
              minHeight: '320px',
            }}
          >
            <Lottie animationData={Unsuccessful} style={{ height: '120px' }} loop={false} />
            <p style={{ fontSize: '14px', fontWeight: '500', marginTop: '10px' }}>
              Payment failed. Please try again later.
            </p>
          </div>
        ) : null}
      </Spin>
    </NachWrapper>
  );
}

export default AutoPayPage;